import { Send, Twitter, Youtube, Instagram } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { scrollToSection } from "@/lib/scroll";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const go = (id) => {
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToSection(id);
    }
  };

  return (
    <footer className="relative border-t border-line/70 pt-20 pb-10 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <img src="/atlas-logo-256.png" alt="Atlas Trading Community logo" className="w-14 h-14 rounded-full ring-1 ring-gold/30 mb-5" data-testid="footer-logo" />
            <p className="font-display font-extrabold tracking-[0.25em] text-4xl sm:text-5xl text-slate-50" data-testid="footer-wordmark">
              ATLAS
            </p>
            <p className="mt-3 font-mono text-[11px] tracking-[0.3em] text-gold/80 uppercase">
              Analysis · Technology · Independence
            </p>
            <p className="mt-6 max-w-md text-[11px] sm:text-xs text-slate-500 leading-relaxed" data-testid="footer-disclaimer">
              Risk warning: trading foreign exchange, gold and CFDs carries a high level of risk and may not be suitable
              for all investors. Past performance is not indicative of future results. Atlas provides education, analysis
              tools and community — nothing on this site constitutes financial advice or a solicitation to trade.
              Never trade with capital you cannot afford to lose.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-5">Navigate</p>
            <ul className="space-y-3">
              {[
                ["how-it-works", "How It Works"],
                ["indicator", "Indicator"],
                ["results", "Results"],
                ["education", "Education"],
                ["community", "Community"],
                ["founder", "Founder Story"],
                ["join", "Join Atlas"],
              ].map(([id, label]) => (
                <li key={id}>
                  <button
                    onClick={() => go(id)}
                    data-testid={`footer-link-${id}`}
                    className="text-sm text-slate-400 hover:text-gold transition-colors duration-300"
                  >
                    {label}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/giveaway"
                  data-testid="footer-link-giveaway"
                  className="text-sm text-slate-400 hover:text-gold transition-colors duration-300"
                >
                  Milestone Giveaway
                </Link>
              </li>
              <li>
                <Link
                  to="/academy/position-sizing"
                  data-testid="footer-link-calculator"
                  className="text-sm text-slate-400 hover:text-gold transition-colors duration-300"
                >
                  Risk Calculator
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-5">Connect</p>
            <div className="flex gap-3">
              {[
                { icon: Send, label: "Telegram", testid: "social-link-telegram", href: "https://t.me/addlist/O9NU4HFXS-cxYzNk" },
                { icon: Twitter, label: "X / Twitter", testid: "social-link-twitter", href: "#" },
                { icon: Youtube, label: "YouTube", testid: "social-link-youtube", href: "#" },
                { icon: Instagram, label: "Instagram", testid: "social-link-instagram", href: "#" },
              ].map((s) => (
                <a
                  key={s.testid}
                  href={s.href}
                  target={s.href === "#" ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={s.testid}
                  className="w-10 h-10 rounded-sm border border-line flex items-center justify-center text-slate-400 hover:text-gold hover:border-gold/50 transition-colors duration-300"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
            <p className="mt-6 text-[11px] text-slate-600 leading-relaxed">
              Disciplined traders.
              <br />
              Built better lives.
            </p>
          </div>
        </div>

        <div className="mt-16 pt-7 border-t border-line/60 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[11px] text-slate-600">© 2026 Atlas. All rights reserved.</p>
          <p className="font-mono text-[10px] tracking-[0.25em] text-slate-600 uppercase">It's a system, not signals.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
